import { eventEmitter, IBaseGameModel } from '@game-studio/arcade-base';
import { BindingModel, bind, bindPassive } from '@game-studio/bindings';
import { SymbolState } from '../services/symbol-service';
import { IDONDServerResponse } from './game-settings';
import { CollectorState } from '../services/collector-service';
import { gameEventEmitter } from '@/src/events';

export enum StateID {
    NOT_FOUND = -1,
    USED = 1,
    NOT_USED = 0,

    NOT_VISIBLE = 0,
    VISIBLE = 1
}

export enum GameState {
    IN_PLAY,
    PAUSED,
    COMPLETE
}

export enum GoldState {
    IDLE,
    TRANS_ON,
    LOOP,
    TRANS_OFF,
    COMPLETE
}

export class GameModel extends BindingModel implements IBaseGameModel {
    public static get Instance(): GameModel {
        return this._instance || (this._instance = new GameModel());
    }
    private static _instance: GameModel;

    public static readonly SPECIAL_SYMBOL = 8;
    public static readonly SYMBOL_COUNT: number = 12;
    public static readonly NUMBER_OF_COLLECTORS = 7;

    constructor() {
        super();
        this.addListeners();
    }

    public payout: number = 0;
    public isNewPlayer: boolean = false;
    public playMode: string;
    public isComplete: boolean;
    public hasBonus: boolean;
    public gameId: number;
    public isInProgress: boolean = false;

    @bindPassive() public collectorStates: number[][] = [
        [0, 0], // x1
        [0, 0], // x2
        [0, 0, 0], // x5
        [0, 0, 0], // x10
        [0, 0, 0, 0], // x20
        [0, 0, 0, 0], // x50
        [0, 0, 0, 0, 0] // x500
    ];

    @bind() public autoPlay: boolean;
    @bind() public gameState: GameState = GameState.COMPLETE;
    @bind() public goldState: GoldState = GoldState.IDLE;

    public ticketSymbols: number[] = [];
    public ticketStates = new Array(GameModel.SYMBOL_COUNT).fill(SymbolState.Available);
    public symbolStates = new Array(GameModel.SYMBOL_COUNT).fill(SymbolState.Available);
    public collectedStates = new Array(GameModel.SYMBOL_COUNT).fill(SymbolState.Available);
    public oneToGoStates = new Array(GameModel.NUMBER_OF_COLLECTORS).fill(CollectorState.Available);

    private _goldenPrize: number = -1;

    public get goldPrize(): number {
        return this._goldenPrize;
    }

    public onNewGame$(): void {
        this.reset();
        this.gameState = GameState.IN_PLAY;
    }

    public setData(responseData: IDONDServerResponse): void {
        this.ticketSymbols = responseData.GameMessage.Symbols;
        this.playMode = responseData.GameMessage.PlayMode;
        this.isComplete = responseData.GameMessage.GameComplete;
        this.payout = responseData.GameMessage.Payout;
        this.hasBonus = false;
        this.gameId = responseData.GameMessage.GameId;

        if (responseData.GameMessage.GoldenBird) {
            this._goldenPrize = responseData.GameMessage.GoldenBird.Bird - 1;
            return;
        }
        this._goldenPrize = -1;
    }

    private reset(): void {
        this.collectorStates = [
            [0, 0], // x1
            [0, 0], // x2
            [0, 0, 0], // x5
            [0, 0, 0], // x10
            [0, 0, 0, 0], // x20
            [0, 0, 0, 0], // x50
            [0, 0, 0, 0, 0] // x500
        ];
        this.ticketStates = new Array(GameModel.SYMBOL_COUNT).fill(SymbolState.Available);
        this.symbolStates = new Array(GameModel.SYMBOL_COUNT).fill(SymbolState.Available);
        this.collectedStates = new Array(GameModel.SYMBOL_COUNT).fill(SymbolState.Available);
        this.oneToGoStates = new Array(GameModel.NUMBER_OF_COLLECTORS).fill(CollectorState.Available);
        this.invalidate(() => this.collectorStates);
    }

    private addListeners(): void {
        gameEventEmitter.on('NEW_GAME', this.onNewGame$.bind(this));
        eventEmitter.on('CABINET_STAKE_CHANGE', this.reset, this);
    }
}

export const gameModel = GameModel.Instance;
